import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, HStack, Input, Select, FormControl, FormLabel,
  Box, Text, Badge, Flex, Table, Thead, Tbody, Tr, Th, Td, IconButton,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../api';

const EVENT_TYPES = [
  { value: 'login_success', label: 'Successful Login', color: 'green' },
  { value: 'login_fail_password', label: 'Failed Login (Password)', color: 'red' },
  { value: 'login_fail_mfa', label: 'Failed Login (MFA)', color: 'orange' },
  { value: 'logout', label: 'Logout', color: 'gray' },
  { value: 'session_expired', label: 'Session Expired', color: 'yellow' },
  { value: 'password_reset_request', label: 'Password Reset Request', color: 'purple' },
  { value: 'password_reset_complete', label: 'Password Reset Complete', color: 'blue' },
  { value: 'mfa_enabled', label: 'MFA Enabled', color: 'teal' },
  { value: 'mfa_disabled', label: 'MFA Disabled', color: 'pink' },
];

const PAGE_SIZES = [10, 100, 500, 1000, 2500, 'all'];

export default function UserActivityModal({ isOpen, onClose }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterIp, setFilterIp] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('per_page', perPage === 'all' ? 'all' : perPage);
      if (filterUser) params.set('user_id', filterUser);
      if (filterType) params.set('event_type', filterType);
      if (filterIp) params.set('ip_address', filterIp);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);

      const { data: res } = await api.get(`/auth/activity?${params.toString()}`);
      setData(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, perPage, filterUser, filterType, filterIp, filterDateFrom, filterDateTo]);

  const fetchUsers = async () => {
    try { const { data } = await api.get('/calendar/users'); setUsers(data); } catch(e) {}
  };

  useEffect(() => { if (isOpen) { fetchUsers(); setPage(1); } }, [isOpen]);
  useEffect(() => { if (isOpen) fetchActivity(); }, [isOpen, fetchActivity]);

  const getEventConfig = (type) => EVENT_TYPES.find(e => e.value === type) || { label: type, color: 'gray' };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) + ' ' +
      dt.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  };

  const resetFilters = () => {
    setFilterUser(''); setFilterType(''); setFilterIp('');
    setFilterDateFrom(''); setFilterDateTo(''); setPage(1);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="95vh" mx={4}>
        <ModalHeader>User Activity Log</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Filters */}
          <Box bg="gray.50" p={3} borderRadius="md" mb={3}>
            <HStack spacing={2} mb={2} flexWrap="wrap">
              <FormControl w="150px">
                <FormLabel fontSize="xs" mb={0.5}>User</FormLabel>
                <Select size="sm" value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}>
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
                </Select>
              </FormControl>
              <FormControl w="200px">
                <FormLabel fontSize="xs" mb={0.5}>Event Type</FormLabel>
                <Select size="sm" value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}>
                  <option value="">All Events</option>
                  <option value="login_success">Successful Logins</option>
                  <option value="login_fail_password,login_fail_mfa">Failed Logins (All)</option>
                  <option value="login_fail_password">Failed - Password</option>
                  <option value="login_fail_mfa">Failed - MFA</option>
                  <option value="logout,session_expired">Logouts / Session Expiry</option>
                  <option value="password_reset_request,password_reset_complete">Password Resets</option>
                  <option value="mfa_enabled,mfa_disabled">MFA Changes</option>
                </Select>
              </FormControl>
              <FormControl w="150px">
                <FormLabel fontSize="xs" mb={0.5}>IP Address</FormLabel>
                <Input size="sm" value={filterIp} placeholder="e.g. 192.168.1.1"
                  onChange={e => { setFilterIp(e.target.value); setPage(1); }} />
              </FormControl>
              <FormControl w="140px">
                <FormLabel fontSize="xs" mb={0.5}>From Date</FormLabel>
                <Input size="sm" type="date" value={filterDateFrom}
                  onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }} />
              </FormControl>
              <FormControl w="140px">
                <FormLabel fontSize="xs" mb={0.5}>To Date</FormLabel>
                <Input size="sm" type="date" value={filterDateTo}
                  onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} />
              </FormControl>
              <Box pt={5}>
                <Button size="xs" variant="ghost" onClick={resetFilters}>Clear Filters</Button>
              </Box>
            </HStack>
            <Flex justify="space-between" align="center">
              <Text fontSize="11px" color="gray.500">{total} records found</Text>
              <HStack spacing={2}>
                <Text fontSize="11px" color="gray.500">Per page:</Text>
                <Select size="xs" w="80px" value={perPage} onChange={e => { setPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setPage(1); }}>
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s === 'all' ? 'All' : s}</option>)}
                </Select>
              </HStack>
            </Flex>
          </Box>

          {/* Table */}
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} bg="white" zIndex={1}>
                <Tr>
                  <Th px={2}>Timestamp</Th>
                  <Th px={2}>User</Th>
                  <Th px={2}>Event</Th>
                  <Th px={2}>IP Address</Th>
                  <Th px={2}>Details</Th>
                  <Th px={2}>User Agent</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr><Td colSpan={6} textAlign="center" py={6} color="gray.400">Loading...</Td></Tr>
                ) : data.length === 0 ? (
                  <Tr><Td colSpan={6} textAlign="center" py={6} color="gray.400">No activity records found</Td></Tr>
                ) : data.map(row => {
                  const cfg = getEventConfig(row.event_type);
                  return (
                    <Tr key={row.id} bg={row.event_type.includes('fail') ? 'red.50' : undefined}
                      _hover={{ bg: 'gray.50' }}>
                      <Td px={2} whiteSpace="nowrap">{fmtDate(row.created_at)}</Td>
                      <Td px={2}>
                        <Text fontWeight="600">{row.display_name || row.username || '—'}</Text>
                      </Td>
                      <Td px={2}>
                        <Badge colorScheme={cfg.color} fontSize="9px">{cfg.label}</Badge>
                      </Td>
                      <Td px={2} fontFamily="mono" fontSize="11px">{row.ip_address}</Td>
                      <Td px={2} maxW="200px" isTruncated color="gray.500">{row.details || '—'}</Td>
                      <Td px={2} maxW="200px" isTruncated fontSize="10px" color="gray.400">{row.user_agent?.slice(0, 60) || '—'}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          {perPage !== 'all' && totalPages > 1 && (
            <Flex justify="space-between" align="center" mt={3} px={2}>
              <Text fontSize="11px" color="gray.500">
                Page {page} of {totalPages} · Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
              </Text>
              <HStack spacing={1}>
                <IconButton icon={<FiChevronLeft />} size="xs" variant="ghost" isDisabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))} aria-label="Previous" />
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pn;
                  if (totalPages <= 5) pn = i + 1;
                  else if (page <= 3) pn = i + 1;
                  else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                  else pn = page - 2 + i;
                  return (
                    <Button key={pn} size="xs" variant={page === pn ? 'solid' : 'ghost'}
                      colorScheme={page === pn ? 'blue' : 'gray'} onClick={() => setPage(pn)}>
                      {pn}
                    </Button>
                  );
                })}
                <IconButton icon={<FiChevronRight />} size="xs" variant="ghost" isDisabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} aria-label="Next" />
              </HStack>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
